import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Logo from "@/components/Logo";
import ReviewHighlight from "@/components/ReviewHighlight";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD =
    "w-full rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none transition-colors duration-200 placeholder:text-muted-foreground focus:border-primary";

export default function QuoteModal({ open, onClose }) {
    const [step, setStep] = useState(0);
    const [done, setDone] = useState(false);
    const [sending, setSending] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", phone: "", agency: "", rate: "", basis: "day", start: "", message: "" });

    useEffect(() => {
        if (open) {
            setStep(0);
            setDone(false);
        }
    }, [open]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const next = () => {
        if (!form.name.trim() || !/.+@.+\..+/.test(form.email) || !form.phone.trim()) {
            toast.error("Please add your name, a valid email and phone number.");
            return;
        }
        setStep(1);
    };

    const submit = async () => {
        if (!form.agency.trim() || !form.rate) {
            toast.error("Please add your recruitment company and contracted rate.");
            return;
        }
        setSending(true);
        try {
            await axios.post(`${API}/quotes`, {
                name: form.name,
                email: form.email,
                phone: form.phone,
                agency: form.agency,
                rate: parseFloat(form.rate),
                rate_basis: form.basis,
                start_date: form.start || null,
                message: form.message || null,
            });
            setDone(true);
            toast.success("Quote request sent — we'll be in touch within one business day.");
        } catch {
            toast.error("Could not send your request. Please call 1800 082 006.");
        } finally {
            setSending(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[80] flex items-end justify-center bg-black/60 p-0 backdrop-blur-sm sm:items-center sm:p-6"
                    onClick={onClose}
                    data-testid="quote-modal-backdrop"
                >
                    <motion.div
                        initial={{ y: 48, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 48, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="w-full max-w-lg rounded-t-3xl border border-border bg-surface p-8 sm:rounded-3xl"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="quote-modal"
                    >
                        <div className="mb-7 flex items-center justify-between">
                            <Logo className="h-9" />
                            <button onClick={onClose} data-testid="quote-close-btn" aria-label="Close" className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:border-primary hover:text-primary">
                                <X size={15} />
                            </button>
                        </div>

                        {done ? (
                            <div className="py-8 text-center" data-testid="quote-success">
                                <CheckCircle2 size={48} className="mx-auto text-primary" />
                                <h3 className="mt-5 font-display text-2xl font-bold">Request received.</h3>
                                <p className="mx-auto mt-3 max-w-sm text-sm text-muted-foreground">
                                    A real human from our family team will call you within one business
                                    day with your tailored quote. Need it sooner? Call{" "}
                                    <a href="tel:1800082006" className="font-semibold text-primary">1800 082 006</a>.
                                </p>
                                <button onClick={onClose} data-testid="quote-done-btn" className="mt-7 rounded-full bg-primary px-7 py-3 text-sm font-bold text-primary-foreground">
                                    Done
                                </button>
                            </div>
                        ) : (
                            <>
                                <h3 className="font-display text-2xl font-bold" data-testid="quote-title">
                                    {step === 0 ? "Let's start with you." : "Now the contract."}
                                </h3>
                                <p className="mt-1.5 text-sm text-muted-foreground">
                                    {step === 0 ? "Step 1 of 2 — your details." : "Step 2 of 2 — the numbers behind your quote."}
                                </p>

                                {step === 0 ? (
                                    <div className="mt-6 space-y-4">
                                        <input data-testid="quote-name-input" className={FIELD} placeholder="Full name" value={form.name} onChange={set("name")} />
                                        <input data-testid="quote-email-input" className={FIELD} type="email" placeholder="Email" value={form.email} onChange={set("email")} />
                                        <input data-testid="quote-phone-input" className={FIELD} type="tel" placeholder="Phone" value={form.phone} onChange={set("phone")} />
                                        <button onClick={next} data-testid="quote-next-btn" className="w-full rounded-full bg-primary py-3.5 text-sm font-bold text-primary-foreground transition-transform duration-200 hover:scale-[1.02] active:scale-95">
                                            Continue
                                        </button>
                                    </div>
                                ) : (
                                    <div className="mt-6 space-y-4">
                                        <input data-testid="quote-agency-input" className={FIELD} placeholder="Recruitment company" value={form.agency} onChange={set("agency")} />
                                        <div className="flex gap-3">
                                            <input data-testid="quote-rate-input" className={FIELD} type="number" min="0" placeholder="Rate (ex GST)" value={form.rate} onChange={set("rate")} />
                                            <select data-testid="quote-basis-select" className={FIELD} value={form.basis} onChange={set("basis")}>
                                                <option value="hour">per hour</option>
                                                <option value="day">per day</option>
                                                <option value="month">per month</option>
                                            </select>
                                        </div>
                                        <input data-testid="quote-start-input" className={FIELD} type="date" value={form.start} onChange={set("start")} aria-label="Contract start date" />
                                        <textarea data-testid="quote-message-input" className={`${FIELD} min-h-[80px] resize-none`} placeholder="Anything else? (optional)" value={form.message} onChange={set("message")} />
                                        <div className="flex gap-3">
                                            <button onClick={() => setStep(0)} data-testid="quote-back-btn" aria-label="Back" className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-border transition-colors hover:border-primary hover:text-primary">
                                                <ArrowLeft size={16} />
                                            </button>
                                            <button onClick={submit} disabled={sending} data-testid="quote-submit-btn" className="flex-1 rounded-full bg-primary py-3.5 text-sm font-bold text-primary-foreground transition-transform duration-200 hover:scale-[1.02] active:scale-95 disabled:opacity-50">
                                                {sending ? "Sending…" : "Request my quote"}
                                            </button>
                                        </div>
                                    </div>
                                )}
                                <ReviewHighlight />
                            </>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
